import { memo } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  Handle,
  Position,
  Panel,
  BackgroundVariant,
  MarkerType,
  type NodeProps,
  type Node,
  type Edge,
  type NodeTypes,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

const C = {
  disease: '#0a104f',
  gene: '#be0080',
  assoc: '#3771c8',
  ddAssoc: '#7a4fa0',
  attrBg: '#eef4fb',
  attrText: '#1a2e44',
  attrBorder: '#d0e3f5',
} as const;

interface AttrGroup {
  label: string;
  attrs: string[];
}

interface HandleDef {
  id: string;
  type: 'source' | 'target';
  pos: 'left' | 'right' | 'top' | 'bottom';
}

interface SchemaEntityData extends Record<string, unknown> {
  title: string;
  subtitle?: string;
  headerColor: string;
  borderColor: string;
  groups: AttrGroup[];
  handles: HandleDef[];
}

type SchemaEntityNodeType = Node<SchemaEntityData, 'schemaEntity'>;

const POS_MAP: Record<string, Position> = {
  left: Position.Left,
  right: Position.Right,
  top: Position.Top,
  bottom: Position.Bottom,
};

const SchemaEntityNode = memo(function SchemaEntityNode({ data }: NodeProps<SchemaEntityNodeType>) {
  const { title, subtitle, headerColor, borderColor, groups, handles } = data;

  return (
    <div
      style={{
        border: `2px solid ${borderColor}`,
        borderRadius: 10,
        background: '#fff',
        width: 250,
        boxShadow: '0 4px 18px rgba(0,0,0,0.10)',
        fontFamily: 'Arial, sans-serif',
        overflow: 'hidden',
      }}
    >
      {handles.map(h => (
        <Handle
          key={h.id}
          id={h.id}
          type={h.type}
          position={POS_MAP[h.pos]}
          style={{ background: borderColor, width: 9, height: 9, border: '2px solid #fff' }}
        />
      ))}

      {/* Header */}
      <div style={{ background: headerColor, padding: '10px 13px' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', lineHeight: 1.3 }}>{title}</div>
        {subtitle && (
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.82)', marginTop: 3 }}>{subtitle}</div>
        )}
      </div>

      {/* Attribute groups */}
      {groups.map((group, gi) => (
        <div key={gi} style={{ padding: gi === 0 ? '9px 11px 5px' : '5px 11px 5px' }}>
          {group.label ? (
            <div
              style={{
                fontSize: 9,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.55px',
                color: borderColor,
                opacity: 0.72,
                marginBottom: 5,
              }}
            >
              {group.label}
            </div>
          ) : null}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            {group.attrs.map((attr, ai) => (
              <span
                key={ai}
                style={{
                  background: C.attrBg,
                  color: C.attrText,
                  fontSize: 10,
                  fontWeight: 500,
                  borderRadius: 4,
                  padding: '2px 7px',
                  border: `1px solid ${C.attrBorder}`,
                  lineHeight: 1.65,
                  whiteSpace: 'nowrap',
                }}
              >
                {attr}
              </span>
            ))}
          </div>
        </div>
      ))}
      <div style={{ height: 10 }} />
    </div>
  );
});

const NODE_TYPES: NodeTypes = {
  schemaEntity: SchemaEntityNode as NodeTypes[string],
};

const SCHEMA_NODES: SchemaEntityNodeType[] = [
  {
    id: 'disease',
    type: 'schemaEntity',
    position: { x: 0, y: 130 },
    data: {
      title: 'Disease',
      headerColor: C.disease,
      borderColor: C.disease,
      handles: [
        { id: 'dis-right', type: 'source', pos: 'right' },
        { id: 'dis-bottom', type: 'source', pos: 'bottom' },
      ],
      groups: [
        {
          label: 'Classification',
          attrs: ['DISGENET type', 'Semantic Type', 'MeSH Class'],
        },
        {
          label: 'Ontology mappings',
          attrs: ['MeSH', 'HPO', 'HDO', 'ICD9-CM', 'OMIM', 'UMLS'],
        },
      ],
    },
  },
  {
    id: 'gene_variant',
    type: 'schemaEntity',
    position: { x: 820, y: 0 },
    data: {
      title: 'Gene / Variant',
      headerColor: C.gene,
      borderColor: C.gene,
      handles: [{ id: 'gv-left', type: 'source', pos: 'left' }],
      groups: [
        {
          label: 'Identifiers',
          attrs: ['Gene symbol & name', 'UniProt acc.', 'Protein class'],
        },
        {
          label: 'Variant',
          attrs: ['Alleles', 'Allelic freq.', 'Consequence', 'Position', 'Class'],
        },
        {
          label: 'Metrics',
          attrs: ['DSI', 'DPI', 'pLi', 'N. Diseases'],
        },
      ],
    },
  },
  {
    id: 'association',
    type: 'schemaEntity',
    position: { x: 415, y: 190 },
    data: {
      title: 'Gene/Variant – Disease',
      subtitle: 'Association  (GDA / VDA)',
      headerColor: C.assoc,
      borderColor: C.assoc,
      handles: [
        { id: 'assoc-left', type: 'target', pos: 'left' },
        { id: 'assoc-right', type: 'target', pos: 'right' },
        { id: 'assoc-bottom', type: 'source', pos: 'bottom' },
      ],
      groups: [
        {
          label: 'Type',
          attrs: ['Association type'],
        },
        {
          label: 'Scores',
          attrs: ['DISGENET Score', 'Evidence Index', 'Evidence Level'],
        },
        {
          label: 'Evidence',
          attrs: ['N. Articles', 'N. SNPs', 'First & last pub. year'],
        },
      ],
    },
  },
  {
    id: 'provenance',
    type: 'schemaEntity',
    position: { x: 415, y: 520 },
    data: {
      title: 'Provenance',
      headerColor: C.assoc,
      borderColor: C.assoc,
      handles: [{ id: 'prov-top', type: 'target', pos: 'top' }],
      groups: [
        {
          label: '',
          attrs: ['Source', 'Article', 'Sentence'],
        },
      ],
    },
  },
  {
    id: 'dd_assoc',
    type: 'schemaEntity',
    position: { x: 0, y: 570 },
    data: {
      title: 'Disease – Disease',
      subtitle: 'Association',
      headerColor: C.ddAssoc,
      borderColor: C.ddAssoc,
      handles: [{ id: 'dd-top', type: 'target', pos: 'top' }],
      groups: [
        {
          label: '',
          attrs: ['Jaccard index & p-value', 'Semantic distance', 'N. shared genes', 'N. shared variants'],
        },
      ],
    },
  },
];

function makeEdge(
  id: string,
  source: string,
  sourceHandle: string,
  target: string,
  targetHandle: string,
  color: string,
  opts: { dashed?: boolean; label?: string } = {},
): Edge {
  return {
    id,
    source,
    sourceHandle,
    target,
    targetHandle,
    type: 'smoothstep',
    markerEnd: { type: MarkerType.ArrowClosed, color, width: 14, height: 14 },
    style: {
      stroke: color,
      strokeWidth: 2,
      ...(opts.dashed ? { strokeDasharray: '5,4' } : {}),
    },
    ...(opts.label
      ? {
          label: opts.label,
          labelStyle: { fontSize: 10, fontWeight: 600, fill: color },
          labelBgStyle: { fill: '#fff', fillOpacity: 0.9 },
          labelBgPadding: [5, 4] as [number, number],
          labelBgBorderRadius: 4,
        }
      : {}),
  };
}

const SCHEMA_EDGES: Edge[] = [
  makeEdge('e-dis-assoc', 'disease', 'dis-right', 'association', 'assoc-left', C.assoc, {
    label: '1 : N',
  }),
  makeEdge('e-gv-assoc', 'gene_variant', 'gv-left', 'association', 'assoc-right', C.assoc, {
    label: '1 : N',
  }),
  makeEdge('e-assoc-prov', 'association', 'assoc-bottom', 'provenance', 'prov-top', C.assoc, {
    dashed: true,
    label: 'supported by',
  }),
  makeEdge('e-dis-dd', 'disease', 'dis-bottom', 'dd_assoc', 'dd-top', C.ddAssoc, {
    label: 'related to',
  }),
];

export default function DisGENETSchemaFlow() {
  return (
    <div
      style={{
        width: '100%',
        height: 840,
        borderRadius: 12,
        overflow: 'hidden',
        border: '1px solid #e0e8f0',
      }}
    >
      <ReactFlow
        nodes={SCHEMA_NODES}
        edges={SCHEMA_EDGES}
        nodeTypes={NODE_TYPES}
        fitView
        fitViewOptions={{ padding: 0.07 }}
        minZoom={0.2}
        maxZoom={2}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        panOnDrag
        zoomOnScroll
        proOptions={{ hideAttribution: true }}
        style={{ background: '#f8fafc' }}
      >
        <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="#dde4ed" />
        <Controls style={{ background: 'transparent', border: 'none', boxShadow: 'none', gap: 4 }} />
        <Panel position="top-left">
          <div
            style={{
              background: 'rgba(255,255,255,0.94)',
              border: '1px solid #e0e8f0',
              borderRadius: 8,
              padding: '9px 14px',
            }}
          >
            <div
              style={{
                fontSize: 9,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                color: '#999',
                marginBottom: 7,
              }}
            >
              Legend
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <LegendItem color={C.assoc} label="GDA / VDA Association" />
              <LegendItem color={C.disease} label="Disease" />
              <LegendItem color={C.gene} label="Gene / Variant" />
              <LegendItem color={C.ddAssoc} label="Disease–Disease Assoc." />
            </div>
          </div>
        </Panel>
      </ReactFlow>
      <style>{`
        .react-flow__controls-button {
          border-radius: 50% !important;
          width: 28px !important;
          height: 28px !important;
          box-shadow: 0 1px 4px rgba(0,0,0,0.14) !important;
          border: 1px solid #e0e8f0 !important;
          background: #fff !important;
          margin: 0 !important;
          padding: 0 !important;
        }
        .react-flow__controls-button:hover { background: #f0f7ff !important; }
        .react-flow__controls-button svg { max-width: 12px; max-height: 12px; }
      `}</style>
    </div>
  );
}

function LegendItem({ color, label }: { color: string; label: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
      <span
        style={{
          width: 11,
          height: 11,
          borderRadius: 3,
          background: color,
          flexShrink: 0,
        }}
      />
      <span style={{ fontSize: 11, color: '#444', fontFamily: 'Arial, sans-serif' }}>{label}</span>
    </div>
  );
}
